
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { sendMagicLink } from "@/utils/auth";
import { toast } from "@/components/ui/use-toast";

// Define the schema for magic link form
const magicLinkFormSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address" }),
});

// Create type from the schema
export type MagicLinkFormValues = z.infer<typeof magicLinkFormSchema>;

interface MagicLinkFormProps {
  onSwitchMode: () => void;
}

export function MagicLinkForm({ onSwitchMode }: MagicLinkFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);

  // Initialize the form
  const form = useForm<MagicLinkFormValues>({
    resolver: zodResolver(magicLinkFormSchema),
    defaultValues: {
      email: "",
    },
  });

  const handleSubmit = async (data: MagicLinkFormValues) => {
    setIsLoading(true);
    
    try {
      await sendMagicLink(data.email);
      setIsSent(true);
      toast({
        title: "Magic Link Sent",
        description: `We've sent a login link to ${data.email}. Please check your inbox.`,
      });
    } catch (error) {
      console.error("Magic link error:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to send magic link. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4 pt-4">
        {!isSent ? (
          <>
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="your.email@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="flex flex-col gap-4 pt-2">
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending link...
                  </span>
                ) : (
                  <>Send Magic Link</>
                )}
              </Button>
              
              <Button type="button" variant="link" onClick={onSwitchMode} className="text-sm text-muted-foreground">
                Back to sign in options
              </Button>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center gap-4 py-6">
            <div className="text-center space-y-2">
              <h3 className="text-lg font-medium">Check your email</h3>
              <p className="text-muted-foreground">
                We've sent a magic link to <span className="font-medium">{form.getValues().email}</span>
              </p>
            </div>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => {
                setIsSent(false);
                form.reset();
              }} 
              className="mt-2"
            >
              Send to a different email
            </Button>
          </div>
        )}
      </form>
    </Form>
  );
}
